import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, X, Radio, Shield, Trash2, Lock, HelpCircle, BookOpen, Brain, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import QATab from "./community/QATab";
import ResourcesTab from "./community/ResourcesTab";
import QuizTab from "./community/QuizTab";
import CreationsTab from "./community/CreationsTab";

function CreateModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/communities", { name, description: desc });
      onCreated(r.data);
      setName(""); setDesc("");
      onClose();
      toast.success("COMMUNITY CREATED");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CREATE FAILED");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="sat-surface sat-brackets border border-[color:var(--sat-cyan)] p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="community-create-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="sat-label sat-cyan flex items-center gap-2"><Users size={12} /> NEW COMMUNITY</div>
          <button data-sfx-silent onClick={onClose} className="text-[color:var(--sat-text-dim)] hover:sat-cyan" data-testid="community-create-close"><X size={14} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="sat-label mb-1 block">NAME</label>
            <input data-testid="community-create-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
              className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm" autoFocus required />
          </div>
          <div>
            <label className="sat-label mb-1 block">DESCRIPTION (optional)</label>
            <textarea data-testid="community-create-desc" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={300} rows={3}
              className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="sat-btn !py-2 !px-3">CANCEL</button>
            <button type="submit" disabled={busy || !name.trim()} data-testid="community-create-submit" className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
              {busy ? "TRANSMITTING…" : "CREATE"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GroupsTab({ user, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/communities");
      setRows(r.data.results || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user]);

  const toggleJoin = async (c) => {
    if (!user) { toast.error("LOG IN TO JOIN"); return; }
    try {
      if (c.is_member) await api.post(`/communities/${c.id}/leave`);
      else await api.post(`/communities/${c.id}/join`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "OPERATION FAILED"); }
  };

  const closeCommunity = async (c) => {
    if (!window.confirm(`Close "${c.name}"? All messages will be deleted.`)) return;
    try {
      await api.delete(`/communities/${c.id}`);
      toast.success("COMMUNITY CLOSED");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "CLOSE FAILED"); }
  };

  return (
    <div data-tab-key="groups" data-tab-content="1" className="space-y-4">
      <div className="flex justify-end">
        {user && (
          <button onClick={() => setShowCreate(true)} className="sat-btn sat-btn-solid" data-testid="community-new-btn">
            <Plus size={13} /> NEW COMMUNITY
          </button>
        )}
      </div>
      <CreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => load()} />
      {loading && <Loading message="LOADING COMMUNITIES" />}
      {!loading && rows.length === 0 && (
        <div className="sat-label text-center py-12" data-testid="community-empty">
          NO COMMUNITIES YET — {user ? "TAP NEW COMMUNITY TO START ONE." : "LOG IN TO CREATE ONE."}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-grid">
        {rows.map(c => (
          <article key={c.id} data-testid={`community-card-${c.id}`}
            className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)] transition-colors p-4 flex flex-col">
            <Link to={`/community/${c.id}`} className="flex-1 mb-3" data-testid={`community-open-${c.id}`}>
              <h2 className="font-display font-bold text-xl mb-1 flex items-center gap-2">
                {c.name}
                {c.archived && (
                  <span className="sat-label border border-[color:var(--sat-live)] text-[color:var(--sat-live)] px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`community-archived-${c.id}`}>
                    <Lock size={8} /> ARCHIVED
                  </span>
                )}
              </h2>
              <p className="text-sm text-[color:var(--sat-text-dim)] line-clamp-3 leading-relaxed min-h-[3rem]">
                {c.description || "—"}
              </p>
              <div className="flex items-center gap-2 mt-3 sat-label text-[color:var(--sat-text-dim)]">
                <Users size={11} /> {c.member_count} MEMBERS
                <span>·</span>
                <span>BY @{c.created_by}</span>
              </div>
            </Link>
            <div className="flex gap-2">
              <button onClick={() => toggleJoin(c)} data-testid={`community-toggle-${c.id}`}
                className={`sat-btn !py-2 !px-3 flex-1 ${c.is_member ? "" : "sat-btn-solid"}`}>
                {c.is_member ? "LEAVE" : "JOIN"}
              </button>
              {isAdmin && (
                <button onClick={() => closeCommunity(c)} data-testid={`community-close-${c.id}`}
                  title="Close community (admin)" className="sat-btn sat-btn-danger !py-2 !px-3">
                  <Shield size={11} /> <Trash2 size={11} />
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: "groups",    label: "GROUPS",    icon: Users,      test: "tab-groups" },
  { key: "qa",        label: "Q&A",       icon: HelpCircle, test: "tab-qa" },
  { key: "resources", label: "RESOURCES", icon: BookOpen,   test: "tab-resources" },
  { key: "creations", label: "CREATIONS", icon: Sparkles,   test: "tab-creations" },
  { key: "quiz",      label: "QUIZ",      icon: Brain,      test: "tab-quiz" },
];

export default function CommunityListPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState(() => {
    const hash = window.location.hash.replace("#", "").split("/")[0];
    return TABS.some(t => t.key === hash) ? hash : "groups";
  });

  return (
    <div data-testid="page-community">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan flex items-center gap-2"><Radio size={12} /> DIRECTIVE // COMMUNITY</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">GROUP TRANSMISSIONS</h1>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2 max-w-3xl">
          Public groups, open Q&amp;A, community-run research resources, and a daily news quiz that rewards
          correct answers with <strong className="sat-cyan">+5 R2-A2 messages</strong> per hit.
          {isAdmin && " Owner-admin can approve, archive, and delete any post."}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="community-hub-tabs">
        {TABS.map(({ key, label, icon: Icon, test }) => (
          <button
            key={key}
            onClick={() => { setTab(key); window.history.replaceState(null, "", `#${key}`); }}
            data-testid={test}
            className={`sat-btn !py-2 !px-4 ${tab === key ? "sat-btn-solid" : ""}`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {tab === "groups"    && <GroupsTab user={user} isAdmin={isAdmin} />}
      {tab === "qa"        && <QATab />}
      {tab === "resources" && <ResourcesTab />}
      {tab === "creations" && <CreationsTab />}
      {tab === "quiz"      && <QuizTab />}
    </div>
  );
}
